/**
 * UI session activity tracking.
 *
 * Fires two lightweight lifecycle events to the backend so the analytics team
 * can identify users who logged in, landed on the performance tab, and left
 * without ever asking a question:
 *   - POST /api/session_activity/start  (once per browser session, right after login)
 *   - POST /api/session_activity/end    (on explicit logout OR pagehide/close)
 *
 * The end event is fired via ``navigator.sendBeacon`` so it survives page
 * teardown. All identity fields are resolved server-side from the auth cookie;
 * this module deliberately does NOT include any email in the request body.
 */
import { getBaseUrl } from "../redux/config";
import { secureFetch } from "./common";

const BROWSER_SESSION_ID_KEY = "ui_browser_session_id";
const SESSION_START_FIRED_KEY = "ui_session_start_fired";
const HAS_PROMPTED_KEY = "ui_had_prompt";
const START_TIME_KEY = "ui_session_start_ms";
// Records the reason (``"close"`` / ``"logout"`` / ``"idle"``) of the last
// end event that was fired for this browser session. Prevents duplicate END
// docs when ``pagehide`` fires more than once (BFCache, mobile visibility
// transitions, etc.).
const END_FIRED_REASON_KEY = "ui_session_end_reason";

/**
 * Return a stable UUID for this browser tab session, minted on first access
 * and persisted in ``sessionStorage`` so it survives soft reloads within the
 * same tab but resets on a new tab / relaunch.
 */
export function getBrowserSessionId() {
  try {
    let sid = sessionStorage.getItem(BROWSER_SESSION_ID_KEY);
    if (!sid) {
      sid =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      sessionStorage.setItem(BROWSER_SESSION_ID_KEY, sid);
    }
    return sid;
  } catch {
    // Private mode / storage disabled — fall back to an ephemeral id.
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
}

/** Flip the "user has fired at least one prompt" flag. Called from ChatFlow. */
export function markPrompted() {
  try {
    sessionStorage.setItem(HAS_PROMPTED_KEY, "true");
  } catch {
    // ignore
  }
}

/** Read the "user has fired at least one prompt" flag. */
export function hasPrompted() {
  try {
    return sessionStorage.getItem(HAS_PROMPTED_KEY) === "true";
  } catch {
    return false;
  }
}

function getStartedAtMs() {
  try {
    const raw = sessionStorage.getItem(START_TIME_KEY);
    const n = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

/**
 * Fire ``ui_session_start`` once per browser session.
 *
 * Safe to call from multiple mount points — the sessionStorage flag guards
 * against duplicate writes on React StrictMode double-invocation, HMR, etc.
 */
export async function postSessionStart(tab = "performance") {
  try {
    if (sessionStorage.getItem(SESSION_START_FIRED_KEY) === "true") {
      return;
    }
    // Mark BEFORE the network call so racing effects can't fire twice.
    sessionStorage.setItem(SESSION_START_FIRED_KEY, "true");
    sessionStorage.setItem(START_TIME_KEY, String(Date.now()));

    const browserSessionId = getBrowserSessionId();
    await secureFetch(`${getBaseUrl()}/api/session_activity/start`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        browser_session_id: browserSessionId,
        tab: tab || "performance",
      }),
    });
  } catch {
    // Best effort — never surface to the user.
  }
}

/**
 * Fire ``ui_session_end`` via ``navigator.sendBeacon`` so the request survives
 * the unload lifecycle. Falls back to a keepalive fetch when sendBeacon is
 * unavailable (older browsers).
 *
 * ``reason``: "logout" | "close" | "idle"
 *
 * Guards (in order):
 *  1. Never mint a new browser_session_id here. If sessionStorage has no id
 *     (e.g. immediately after ``clearSessionActivityState`` runs during the
 *     logout redirect), there's nothing to end — return silently. This
 *     prevents "phantom" end docs with UUIDs that never had a start.
 *  2. Never fire without a matching ``ui_session_start`` — same reason.
 *  3. Dedupe by reason. ``pagehide`` fires on real unloads AND on BFCache /
 *     mobile visibility transitions, so it can trigger multiple times per
 *     live tab. Each reason may fire at most once; explicit ``logout`` may
 *     additionally override an earlier ``close``/``idle`` so the intent is
 *     always captured.
 */
export function sendSessionEnd(reason = "close", lastTab = "performance") {
  try {
    // (1) No minting — read from sessionStorage directly.
    let browserSessionId = null;
    try {
      browserSessionId = sessionStorage.getItem(BROWSER_SESSION_ID_KEY);
    } catch {
      browserSessionId = null;
    }
    if (!browserSessionId) return;

    // (2) No end without start.
    try {
      if (sessionStorage.getItem(SESSION_START_FIRED_KEY) !== "true") return;
    } catch {
      return;
    }

    // (3) Dedupe by reason. logout can override an earlier close/idle;
    // everything else is one-shot per reason per browser session.
    let prevReason = null;
    try {
      prevReason = sessionStorage.getItem(END_FIRED_REASON_KEY);
    } catch {
      prevReason = null;
    }
    if (prevReason) {
      if (reason !== "logout") return;
      if (prevReason === "logout") return;
    }
    // Mark BEFORE the network call so racing pagehide/visibility firings
    // dedupe correctly even if the beacon is still in flight.
    try {
      sessionStorage.setItem(END_FIRED_REASON_KEY, reason);
    } catch {
      // ignore
    }

    const startedAt = getStartedAtMs();
    const durationMs = startedAt ? Math.max(0, Date.now() - startedAt) : null;

    const body = JSON.stringify({
      browser_session_id: browserSessionId,
      reason,
      last_tab: lastTab || "performance",
      had_prompt: hasPrompted(),
      duration_ms: durationMs,
    });

    const url = `${getBaseUrl()}/api/session_activity/end`;

    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      // Blob w/ application/json so FastAPI parses it as JSON.
      const blob = new Blob([body], { type: "application/json" });
      const ok = navigator.sendBeacon(url, blob);
      if (ok) return;
    }

    // Fallback for browsers without sendBeacon — keepalive fetch keeps the
    // request alive across unload (best effort; not guaranteed).
    secureFetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Best effort — never throw during unload.
  }
}

/** Clear all session-activity state (used on logout, after the beacon fires). */
export function clearSessionActivityState() {
  try {
    sessionStorage.removeItem(BROWSER_SESSION_ID_KEY);
    sessionStorage.removeItem(SESSION_START_FIRED_KEY);
    sessionStorage.removeItem(HAS_PROMPTED_KEY);
    sessionStorage.removeItem(START_TIME_KEY);
    sessionStorage.removeItem(END_FIRED_REASON_KEY);
  } catch {
    // ignore
  }
}
